// LoanTable.jsx (PROD) — aligned with UAT CE fields
import React from 'react';
import LoanPagination from './LoanPagination';
import usePagination from '../hooks/usePagination';

function formatDateTime(datetime) {
  if (!datetime || datetime === '1970-01-01 00:00:00' || datetime === '0000-00-00 00:00:00') return '-';
  const date = new Date(datetime);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function formatTime(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const [hours, minutes] = timeStr.split(':');
  if (!hours || !minutes) return '';
  const date = new Date();
  date.setHours(parseInt(hours));
  date.setMinutes(parseInt(minutes));
  date.setSeconds(0);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatDateOnly(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function LoanTable({
  currentUser, loans, filterKey, newRows, editingId, editingData,
  onEditChange, onStartEdit, onSaveEdit, onCancelEdit,
  onNewRowChange, onNewRowSave, onNewRowCancel,
  onToggleVisibility,
  onArchiveLoan, // keep
  // NEW for multi-select archive:
  selectedLoans = [],
  onToggleSelectLoan,
  onArchiveSelectedLoans
}) {
  const renderRegionOptions = () => {
    const regions = [
      "NCR","CAR","Region I","Region II","Region III","Region IV-A","Region IV-B","Region V","Region VI","Region VII",
      "Region VIII","Region IX","Region X","Region XI","Region XII","Region XIII","BARMM"
    ];
    return regions.map(region => <option key={region} value={region}>{region}</option>);
  };

  // CE dropdown options CE00..CE20
  const renderCeOptions = () =>
    Array.from({ length: 21 }, (_, i) => {
      const code = `CE${i.toString().padStart(2, '0')}`;
      return <option key={code} value={code}>{code}</option>;
    });

  const NoEntry = () => (
    <span style={{ opacity: 0.6, fontStyle: 'italic' }}>No entry yet</span>
  );

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const isStandard = currentUser?.role === 'standard';
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const filteredLoans = isAdmin
    ? loans
    : loans.filter(loan => loan.loan_status !== 'Disbursed' || loan.show_to_standard === 'yes');

  const pagination = usePagination(filteredLoans, filterKey);

  return (
    <div className="user-table-container">
      <LoanPagination {...pagination} />
      {/* NEW: bulk archive button (top, only for superadmin) */}
      {isSuperAdmin && (
        <div
          style={{
            marginBottom: '0.75rem',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <button
            onClick={onArchiveSelectedLoans}
            disabled={!selectedLoans || selectedLoans.length === 0}
            className="btn btn-sm btn-danger"
          >
            Archive Selected ({selectedLoans?.length || 0})
          </button>
        </div>
      )}

      <table className="user-table">
        <thead>
          <tr>
            <th>Type of Loan</th>
            <th>Approval Date</th>
            <th>Approval Time</th>
            <th>Region</th>
            {isAdmin && <th>Employee ID</th>}
            {isAdmin && <th>Client Name</th>}
            {isAdmin && <th>Initial</th>}
            {isStandard && <th>Client</th>}
            {isAdmin && <th>Verifier</th>}
            <th>Status</th>
            <th>Remarks</th>
            {isAdmin && <th>CE Code LMS</th>}
            <th>CE Code NOAH</th> {/* visible to everyone */}
            <th>Date and Time Disbursed</th>
            {isAdmin && <th>Time and Date (Time Stamp)</th>}
            {isAdmin && <th>Updated By</th>}
            {isAdmin && <th>Action</th>}
            {isSuperAdmin && <th>Archive</th>}
          </tr>
        </thead>

        <tbody>
          {/* New rows (admin only) */}
          {isAdmin && newRows.map((row, idx) => (
            <tr key={`new-${idx}`}>
              <td>
                <select value={row.type_of_loan} onChange={e => onNewRowChange(idx, 'type_of_loan', e.target.value)}>
                  <option value="">--Select--</option>
                  <option>Regular Loan</option>
                  <option>PL</option>
                  <option>SLI</option>
                  <option>PL Convertion</option>
                </select>
              </td>
              <td>
                <input type="date" value={row.approval_date} onChange={e => onNewRowChange(idx, 'approval_date', e.target.value)} />
              </td>
              <td>
                <input type="time" value={row.approval_time} onChange={e => onNewRowChange(idx, 'approval_time', e.target.value)} />
              </td>
              <td>
                <select value={row.region} onChange={e => onNewRowChange(idx, 'region', e.target.value)}>
                  <option value="">--Select Region--</option>
                  {renderRegionOptions()}
                </select>
              </td>
              <td><input type="text" value={row.employee_id} onChange={e => onNewRowChange(idx, 'employee_id', e.target.value)} /></td>
              <td><input type="text" value={row.client_name} onChange={e => onNewRowChange(idx, 'client_name', e.target.value)} /></td>
              <td><input type="text" value={row.initial} onChange={e => onNewRowChange(idx, 'initial', e.target.value)} /></td>
              {isAdmin && (
                <td><input type="text" value={row.verifier} onChange={e => onNewRowChange(idx, 'verifier', e.target.value)} /></td>
              )}
              <td>
                <select value={row.loan_status} onChange={e => onNewRowChange(idx, 'loan_status', e.target.value)}>
                  <option value="">--Select--</option>
                  <option>Pending</option>
                  <option>Processing</option>
                  <option>Reprocessing</option>
                  <option>For Final Checking</option>
                  <option>Not Yet Started</option>
                  <option>Hold</option>
                  <option>Exceeds 30 Days Validity</option>
                  <option>Disapproved</option>
                  <option>Disbursed</option>
                  <option>Cancelled</option>
                </select>
              </td>
              <td><input type="text" value={row.remarks} onChange={e => onNewRowChange(idx, 'remarks', e.target.value)} /></td>

              {/* CE Code LMS (admin only) */}
              <td>
                <select value={row.ce_code || ''} onChange={e => onNewRowChange(idx, 'ce_code', e.target.value)}>
                  <option value="">--Select--</option>
                  {renderCeOptions()}
                </select>
              </td>

              {/* CE Code NOAH (everyone) */}
              <td>
                <select value={row.ce_code_noah || ''} onChange={e => onNewRowChange(idx, 'ce_code_noah', e.target.value)}>
                  <option value="">--Select--</option>
                  {renderCeOptions()}
                </select>
              </td>

              <td>
                <input
                  type="datetime-local"
                  value={row.disbursed_date || ''}
                  onChange={e => onNewRowChange(idx, 'disbursed_date', e.target.value)}
                />
              </td>

              <td>
                <select value={row.show_to_standard || 'yes'} onChange={e => onNewRowChange(idx, 'show_to_standard', e.target.value)}>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </td>

              <td><input type="text" value={row.noah_entry || ''} onChange={e => onNewRowChange(idx, 'noah_entry', e.target.value)} /></td>
              <td><input type="text" value={row.record_position || ''} onChange={e => onNewRowChange(idx, 'record_position', e.target.value)} /></td>
              <td>{formatDateTime(row.time_and_date)}</td>
              <td>
                <button onClick={() => onNewRowSave(row, idx)}>Save</button>
                <button onClick={() => onNewRowCancel(idx)} style={{ marginLeft: 4 }}>Cancel</button>
              </td>
            </tr>
          ))}

          {/* Existing loans */}
          {pagination.rows.map((loan) => {
            if (loan.loan_status === 'Disbursed') return null;
            const isEditing = editingId === loan.id && isAdmin;
            const isSelected = selectedLoans?.includes(loan.id);

            return (
              <tr key={loan.id}>
                {isEditing ? (
                  <>
                    <td>
                      <select value={editingData.type_of_loan} onChange={e => onEditChange('type_of_loan', e.target.value)}>
                        <option>Regular Loan</option>
                        <option>PL</option>
                        <option>SLI</option>
                        <option>PL Convertion</option>
                      </select>
                    </td>
                    <td><input type="date" value={editingData.approval_date} onChange={e => onEditChange('approval_date', e.target.value)} /></td>
                    <td><input type="time" value={editingData.approval_time || ''} onChange={e => onEditChange('approval_time', e.target.value)} /></td>
                    <td>
                      <select value={editingData.region} onChange={e => onEditChange('region', e.target.value)}>
                        {renderRegionOptions()}
                      </select>
                    </td>
                    <td><input type="text" value={editingData.employee_id} onChange={e => onEditChange('employee_id', e.target.value)} /></td>
                    <td><input type="text" value={editingData.client_name} onChange={e => onEditChange('client_name', e.target.value)} /></td>
                    <td><input type="text" value={editingData.initial} onChange={e => onEditChange('initial', e.target.value)} /></td>
                    <td><input type="text" value={editingData.verifier} onChange={e => onEditChange('verifier', e.target.value)} /></td>
                    <td>
                      <select value={editingData.loan_status} onChange={e => onEditChange('loan_status', e.target.value)}>
                        <option>Pending</option>
                        <option>Processing</option>
                        <option>Reprocessing</option>
                        <option>For Final Checking</option>
                        <option>Not Yet Started</option>
                        <option>Hold</option>
                        <option>Exceeds 30 Days Validity</option>
                        <option>Disapproved</option>
                        <option>Disbursed</option>
                        <option>Cancelled</option>
                      </select>
                    </td>
                    <td><input type="text" value={editingData.remarks} onChange={e => onEditChange('remarks', e.target.value)} /></td>

                    {/* CE Code LMS (admin only) */}
                    <td>
                      <select value={editingData.ce_code || ''} onChange={e => onEditChange('ce_code', e.target.value)}>
                        <option value="">--Select--</option>
                        {renderCeOptions()}
                      </select>
                    </td>

                    {/* CE Code NOAH (everyone) */}
                    <td>
                      <select value={editingData.ce_code_noah || ''} onChange={e => onEditChange('ce_code_noah', e.target.value)}>
                        <option value="">--Select--</option>
                        {renderCeOptions()}
                      </select>
                    </td>

                    <td>
                      <input
                        type="datetime-local"
                        value={editingData.disbursed_date || ''}
                        onChange={e => onEditChange('disbursed_date', e.target.value)}
                      />
                    </td>

                    <td>
                      <select value={editingData.show_to_standard || 'yes'} onChange={e => onEditChange('show_to_standard', e.target.value)}>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </td>

                    <td><input type="text" value={editingData.noah_entry || ''} onChange={e => onEditChange('noah_entry', e.target.value)} /></td>
                    <td><input type="text" value={editingData.record_position || ''} onChange={e => onEditChange('record_position', e.target.value)} /></td>
                    <td>{formatDateTime(editingData.time_and_date)}</td>
                    <td>{editingData.updated_by || '-'}</td>
                    <td>
                      <button onClick={onSaveEdit}>Save</button>
                      <button onClick={onCancelEdit} style={{ marginLeft: 4 }}>Cancel</button>
                    </td>
                    {isSuperAdmin && <td>{/* keep cell for alignment */}</td>}
                  </>
                ) : (
                  <>
                    <td>{loan.type_of_loan}</td>
                    <td>{formatDateOnly(loan.approval_date)}</td>
                    <td>{formatTime(loan.approval_time)}</td>
                    <td>{loan.region}</td>
                    {isAdmin && <td>{loan.employee_id}</td>}
                    {isAdmin && <td>{loan.client_name}</td>}
                    {isAdmin ? <td>{loan.initial}</td> : <td>{loan.client_name}</td>}
                    {isAdmin && <td>{loan.verifier}</td>}
                    <td><span className={`status-badge ${loan.loan_status?.toLowerCase().replace(/\s/g, '-')}`}>{loan.loan_status}</span></td>
                    <td>{loan.remarks}</td>

                    {/* Display CE Code LMS (admin only) with fallback */}
                    {isAdmin && <td>{loan.ce_code ? loan.ce_code : <NoEntry />}</td>}

                    {/* Display CE Code NOAH (everyone) with fallback */}
                    <td>{loan.ce_code_noah ? loan.ce_code_noah : <NoEntry />}</td>

                    <td>{formatDateTime(loan.disbursed_date)}</td>
                    {isAdmin && <td>{formatDateTime(loan.time_and_date)}</td>}
                    {isAdmin && <td>{loan.updated_by || '-'}</td>}
                    {isAdmin && <td><button onClick={() => onStartEdit(loan.id)}>Edit</button></td>}

                    {isSuperAdmin && (
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => onArchiveLoan(loan.id)}
                            className="btn btn-sm btn-danger"
                            title="Archive this loan"
                          >
                            Archive
                          </button>

                          {onToggleSelectLoan && (
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '0.8rem',
                                cursor: 'pointer'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={!!isSelected}
                                onChange={() => onToggleSelectLoan(loan.id)}
                              />
                              <span>Select</span>
                            </label>
                          )}
                        </div>
                      </td>
                    )}
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <LoanPagination {...pagination} />
    </div>
  );
}

export default LoanTable;
